/**
 * Modules, in the app.
 *
 * A feature declares its own screens, its own menu entries and the permission
 * each one needs. The shell then builds the navigation and the router from that
 * list, so nothing central has to be edited to add a feature - and a person only
 * ever sees the parts of the app they are actually allowed to use.
 */
import type { ReactNode } from 'react';
import type { Permission, StoreSettings } from '@pos/shared';
import type { StringKey } from './i18n/en.js';

/**
 * A feature the shop can switch off in Settings. The screen stays registered
 * and the server keeps answering - it simply stops being offered, so switching
 * it back on is one tick and nothing was lost in the meantime.
 */
export type FeatureSetting = 'shiftsEnabled' | 'holdSalesEnabled' | 'customerTabsEnabled';

export type NavGroup = 'sell' | 'stock' | 'money' | 'setup';

export interface NavItem {
  to: string;
  /** Key into the translation files - never a literal string. */
  labelKey: StringKey;
  icon: string;
  group: NavGroup;
  /**
   * Position in the main menu. Only the handful of screens a shop touches every
   * day belong here; everything else is one tap away on More, which keeps the
   * menu short enough to read at a glance.
   */
  primary?: number;
  /** One plain line saying what the screen is for. Shown on the More page. */
  hintKey?: StringKey;
  /** Hidden entirely when the person does not hold this. */
  permission?: Permission;
  /** Any one of these is enough. */
  anyPermission?: Permission[];
  /** Hidden while this setting is off. */
  requiresSetting?: FeatureSetting;
  /** Position in the phone bottom bar; left out means it is not there. */
  bottom?: number;
  /** Lower sorts first inside its group. */
  order?: number;
}

export interface RouteDef {
  path: string;
  element: ReactNode;
  permission?: Permission;
  anyPermission?: Permission[];
  /** Answers with "this is switched off" while this setting is off. */
  requiresSetting?: FeatureSetting;
  /** Shown in the header. */
  titleKey?: StringKey;
}

export interface WebModule {
  id: string;
  nameKey?: StringKey;
  nav?: NavItem[];
  routes: RouteDef[];
  /** Sorts the modules; lower comes first in the menu. */
  order?: number;
}

const registry: WebModule[] = [];

export function registerModule(m: WebModule): WebModule {
  /*
   * A module is its id, not the number of times its file happened to run.
   *
   * The dev server re-executes a module file on every hot reload, and pushing
   * blindly stacked a second copy of every screen on top of the first: the menu
   * grew duplicate entries, and two routes claiming one path made React Router
   * remount the screen - which quietly emptied a sale being picked back up.
   * Replacing by id makes registering twice mean the same as registering once.
   */
  const existing = registry.findIndex((x) => x.id === m.id);
  if (existing >= 0) registry[existing] = m;
  else registry.push(m);
  return m;
}

export function webModules(): WebModule[] {
  return [...registry].sort((a, b) => (a.order ?? 100) - (b.order ?? 100));
}

export function allRoutes(): RouteDef[] {
  return webModules().flatMap((m) => m.routes);
}

/** The short everyday menu, in the order it is pinned. */
export function primaryNav(): NavItem[] {
  return allNav()
    .filter((i) => i.primary)
    .sort((a, b) => (a.primary ?? 0) - (b.primary ?? 0));
}

export function allNav(): NavItem[] {
  return webModules()
    .flatMap((m) => m.nav ?? [])
    .sort((a, b) => (a.order ?? 100) - (b.order ?? 100));
}

/** Is the feature this screen belongs to switched on in this shop? */
export function featureOn(
  item: { requiresSetting?: FeatureSetting },
  settings: StoreSettings,
): boolean {
  return !item.requiresSetting || settings[item.requiresSetting] === true;
}

/** Does this person hold what the item asks for? */
export function itemAllowed(
  item: { permission?: Permission; anyPermission?: Permission[] },
  can: (p: Permission) => boolean,
): boolean {
  if (item.permission && !can(item.permission)) return false;
  if (item.anyPermission && !item.anyPermission.some(can)) return false;
  return true;
}

export const NAV_GROUP_LABEL: Record<NavGroup, StringKey> = {
  sell: 'nav.groupSell',
  stock: 'nav.groupStock',
  money: 'nav.groupMoney',
  setup: 'nav.groupSetup',
};
